import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { tokenStorage } from '../api/client';
import { authApi } from '../api/auth';
import { klienApi } from '../api/klien';
import type { KlienInfo, KlienMeResponse, LoginRequest } from '../types/api';

interface AuthState {
  isInitialized: boolean;
  isAuthenticated: boolean;
  klien: KlienInfo | null;
}

interface AuthContextValue extends AuthState {
  login: (body: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshKlien: () => Promise<KlienMeResponse | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isInitialized: false,
    isAuthenticated: false,
    klien: null,
  });

  // Saat mount, kalau ada token coba fetch /klien/me → kalau sukses, authenticated
  useEffect(() => {
    const bootstrap = async () => {
      const token = tokenStorage.getAccessToken();
      if (!token) {
        setState({ isInitialized: true, isAuthenticated: false, klien: null });
        return;
      }

      try {
        const me = await klienApi.me();
        setState({
          isInitialized: true,
          isAuthenticated: true,
          klien: {
            kodeKlien: me.kodeKlien,
            username: me.username,
            email: me.email,
            namaLengkap: me.namaLengkap,
          },
        });
      } catch {
        tokenStorage.clear();
        setState({ isInitialized: true, isAuthenticated: false, klien: null });
      }
    };

    bootstrap();
  }, []);

  const login = async (body: LoginRequest) => {
    const result = await authApi.login(body);
    tokenStorage.setTokens(result.accessToken, result.refreshToken);
    setState({
      isInitialized: true,
      isAuthenticated: true,
      klien: result.klien,
    });
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore — logout tetap berjalan di sisi client
    }
    tokenStorage.clear();
    setState({
      isInitialized: true,
      isAuthenticated: false,
      klien: null,
    });
  };

  const refreshKlien = async (): Promise<KlienMeResponse | null> => {
    try {
      const me = await klienApi.me();
      setState((prev) => ({
        ...prev,
        klien: {
          kodeKlien: me.kodeKlien,
          username: me.username,
          email: me.email,
          namaLengkap: me.namaLengkap,
        },
      }));
      return me;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshKlien }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth harus dipakai di dalam <AuthProvider>');
  }
  return ctx;
}
